import React, { useRef, useState, useEffect } from 'react';

interface DoubleBezelCardProps {
  children: React.ReactNode;
  className?: string;
  innerClassName?: string;
  tilt?: boolean;
  spotlight?: boolean;
  glowColor?: string;
}

export const DoubleBezelCard: React.FC<DoubleBezelCardProps> = ({
  children,
  className = '',
  innerClassName = '',
  tilt = false,
  spotlight = true,
  glowColor = 'rgba(212, 175, 55, 0.15)',
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [rotX, setRotX] = useState<number>(0);
  const [rotY, setRotY] = useState<number>(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isFinePointer, setIsFinePointer] = useState<boolean>(false);

  useEffect(() => {
    const mq = window.matchMedia('(pointer: fine)');
    setIsFinePointer(mq.matches);

    const handleMqChange = (e: MediaQueryListEvent) => setIsFinePointer(e.matches);
    mq.addEventListener('change', handleMqChange);

    const handleScroll = () => {
      setIsHovered((prev) => (prev ? false : prev));
      setRotX((prev) => (prev !== 0 ? 0 : prev));
      setRotY((prev) => (prev !== 0 ? 0 : prev));
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      mq.removeEventListener('change', handleMqChange);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isFinePointer || !cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setMousePos({ x, y });

    if (tilt) {
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const maxTilt = 4; // subtle high-end tilt
      const rY = ((x - centerX) / centerX) * maxTilt;
      const rX = -((y - centerY) / centerY) * maxTilt;
      setRotX(rX);
      setRotY(rY);
    }
  };

  const handleMouseEnter = () => {
    if (isFinePointer) setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setRotX(0);
    setRotY(0);
  };

  const transformStyle = isFinePointer && tilt
    ? {
        transform: `perspective(1000px) rotateX(${rotX}deg) rotateY(${rotY}deg)`,
        transition: isHovered ? 'transform 0.1s ease-out' : 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
      }
    : {};

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={transformStyle}
      className={`double-bezel-outer p-[1.5px] rounded-[1.75rem] transition-shadow duration-500 ${className}`}
    >
      <div
        className={`double-bezel-inner relative overflow-hidden rounded-[calc(1.75rem-1.5px)] bg-[#0e0e13] ${innerClassName}`}
      >
        {/* Dynamic Spotlight Glare on Hover */}
        {spotlight && isHovered && (
          <div
            className="pointer-events-none absolute -inset-px transition-opacity duration-300 opacity-100"
            style={{
              background: `radial-gradient(600px circle at ${mousePos.x}px ${mousePos.y}px, ${glowColor}, transparent 45%)`,
            }}
          />
        )}

        {/* Content */}
        <div className="relative z-10">{children}</div>
      </div>
    </div>
  );
};
